/**
 * TASK-DEATH-002: Death Sequence Manager
 *
 * Manages the death animation and cleanup sequence for combat units.
 * Coordinates phases: Death Animation -> Smoke Effect -> Cleanup
 */

import type { AxialCoordinate } from '@drawn-of-war/shared';

/**
 * Death sequence phases
 */
export enum DeathPhase {
  DEATH_ANIMATION = 'death_animation',
  SMOKE_EFFECT = 'smoke_effect',
  COMPLETE = 'complete'
}

/**
 * Configuration for death sequence timing
 */
export interface DeathSequenceConfig {
  animationDurationMs?: number;
  smokeDurationMs?: number;
}

/**
 * Default timing constants
 */
const DEFAULT_ANIMATION_DURATION_MS = 500;
const DEFAULT_SMOKE_DURATION_MS = 300;

/**
 * Internal state for a dying unit
 * L3-ELEMENT-DEATH: Now includes element for death effect visuals
 */
interface DyingUnitState {
  unitId: string;
  position: AxialCoordinate;
  phase: DeathPhase;
  phaseStartTime: number;
  phaseElapsed: number;
  animationDurationMs: number;
  smokeDurationMs: number;
  element: string;  // L3-ELEMENT-DEATH: Element type for death effects
}

/**
 * Callbacks for death sequence events
 * L3-ELEMENT-DEATH: onSmokeEffectStart now includes element
 */
export interface DeathSequenceCallbacks {
  onDeathAnimationStart: (unitId: string, position: AxialCoordinate) => void;
  onSmokeEffectStart: (unitId: string, position: AxialCoordinate, element: string) => void;
  onDeathComplete: (unitId: string) => void;
}

/**
 * DeathSequenceManager
 *
 * Orchestrates the complete death sequence for units.
 */
export class DeathSequenceManager {
  private dyingUnits: Map<string, DyingUnitState> = new Map();
  private callbacks: DeathSequenceCallbacks;

  constructor(callbacks: DeathSequenceCallbacks) {
    this.callbacks = callbacks;
  }

  /**
   * Start death sequence for a unit
   * L3-ELEMENT-DEATH: Now accepts element for death effect visuals
   */
  public startDeathSequence(
    unitId: string,
    position: AxialCoordinate,
    config?: DeathSequenceConfig,
    element?: string
  ): void {
    // Don't re-trigger for already dying unit
    if (this.dyingUnits.has(unitId)) {
      return;
    }

    const state: DyingUnitState = {
      unitId,
      position,
      phase: DeathPhase.DEATH_ANIMATION,
      phaseStartTime: Date.now(),
      phaseElapsed: 0,
      animationDurationMs: config?.animationDurationMs ?? DEFAULT_ANIMATION_DURATION_MS,
      smokeDurationMs: config?.smokeDurationMs ?? DEFAULT_SMOKE_DURATION_MS,
      element: element || 'physical'  // Default to physical for non-elemental deaths
    };

    this.dyingUnits.set(unitId, state);
    this.callbacks.onDeathAnimationStart(unitId, position);
  }

  /**
   * Update all dying units
   * @param deltaTimeMs Time elapsed since last update in milliseconds
   */
  public update(deltaTimeMs: number): void {
    const unitsToRemove: string[] = [];

    for (const [unitId, state] of this.dyingUnits.entries()) {
      state.phaseElapsed += deltaTimeMs;

      // Check for phase transitions
      if (state.phase === DeathPhase.DEATH_ANIMATION) {
        if (state.phaseElapsed >= state.animationDurationMs) {
          // Transition to smoke effect
          state.phase = DeathPhase.SMOKE_EFFECT;
          state.phaseElapsed = state.phaseElapsed - state.animationDurationMs;
          // L3-ELEMENT-DEATH: Pass element for element-specific death effects
          this.callbacks.onSmokeEffectStart(unitId, state.position, state.element);
        }
      } else if (state.phase === DeathPhase.SMOKE_EFFECT) {
        if (state.phaseElapsed >= state.smokeDurationMs) {
          // Transition to complete
          state.phase = DeathPhase.COMPLETE;
          this.callbacks.onDeathComplete(unitId);
        }
      } else if (state.phase === DeathPhase.COMPLETE) {
        // Mark for removal
        unitsToRemove.push(unitId);
      }
    }

    // Remove completed units
    for (const unitId of unitsToRemove) {
      this.dyingUnits.delete(unitId);
    }
  }

  /**
   * Check if a unit is currently in death sequence
   */
  public isUnitDying(unitId: string): boolean {
    return this.dyingUnits.has(unitId);
  }

  /**
   * Get current phase for a dying unit
   */
  public getPhase(unitId: string): DeathPhase | null {
    const state = this.dyingUnits.get(unitId);
    return state ? state.phase : null;
  }

  /**
   * Get progress (0-1) through current phase
   */
  public getProgress(unitId: string): number | null {
    const state = this.dyingUnits.get(unitId);
    if (!state) {
      return null;
    }

    if (state.phase === DeathPhase.DEATH_ANIMATION) {
      return Math.min(1, state.phaseElapsed / state.animationDurationMs);
    } else if (state.phase === DeathPhase.SMOKE_EFFECT) {
      return Math.min(1, state.phaseElapsed / state.smokeDurationMs);
    }

    return 1; // Complete
  }

  /**
   * Get all dying unit IDs
   */
  public getDyingUnitIds(): string[] {
    return Array.from(this.dyingUnits.keys());
  }

  /**
   * Clear all dying units (for combat end/cleanup)
   */
  public clear(): void {
    this.dyingUnits.clear();
  }
}
